import {
  cannectDefaultProduct,
  cannectDefaultProductCapsule,
  cannectDefaultProductCream,
  cannectDefaultProductGummy,
  cannectDefaultProductNoTarg,
  cannectDefaultProductOil
} from '@cannect/assets/img'
import { useState } from 'react'
import { pickProductCoverImagePath, ProductMediaItem } from 'utils/productMedia'
import { BaseRecipeProduct } from '..'
import * as Style from './styles'

type RecipeProductProps = {
  product: BaseRecipeProduct
}

const formatBrl = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)

function normalizeShape(name: string | null | undefined): string {
  if (!name) return ''
  return name
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

function toMediaList(media: BaseRecipeProduct['media']): ProductMediaItem[] {
  if (!media) return []
  return Array.isArray(media) ? media : [media]
}

function resolveProductImage(product: BaseRecipeProduct): string {
  const { origin, class_id, supplier_id, shape_name, media } = product

  const defaultImage = supplier_id === 18 || class_id === 6 ? cannectDefaultProduct : cannectDefaultProductNoTarg

  const realImage = pickProductCoverImagePath(toMediaList(media))

  if (origin === 'pill') {
    return realImage || defaultImage
  }

  if (class_id === 6) {
    return realImage || defaultImage
  }

  // Cannabis: sempre genérica por forma — nunca exibe a imagem real
  const shape = normalizeShape(shape_name)
  switch (shape) {
    case 'oleo':
      return cannectDefaultProductOil
    case 'capsula':
      return cannectDefaultProductCapsule
    case 'creme':
      return cannectDefaultProductCream
    case 'goma':
      return cannectDefaultProductGummy
    default:
      return defaultImage
  }
}

export default function RecipeProduct({ product }: RecipeProductProps) {
  const rawBottles = product.required_bottles
  const bottles = rawBottles != null && Number(rawBottles) > 0 ? Number(rawBottles) : 1
  const listUnit = product.price
  const payUnit = product.promotional_price ?? product.price
  const hasDiscount = payUnit < listUnit - 0.005
  const discountPercent = hasDiscount ? Math.round(((listUnit - payUnit) / listUnit) * 100) : 0

  const defaultFallback =
    product.supplier_id === 18 || product.class_id === 6 ? cannectDefaultProduct : cannectDefaultProductNoTarg
  const [imgSrc, setImgSrc] = useState(() => resolveProductImage(product))

  return (
    <Style.RecipeProductContainer>
      <img
        src={imgSrc}
        alt={product.translated_name}
        onError={() => setImgSrc(defaultFallback)}
        className="h-20 w-20 shrink-0 rounded-md object-contain"
      />
      <Style.ProductMainInfo>
        <p>
          <strong>{product.translated_name}</strong>
        </p>
        <p>{product.prescription}</p>
        <span>{bottles} frasco(s)</span>
      </Style.ProductMainInfo>
      <Style.PriceContainer>
        <p>
          <strong>Unidade: </strong>
          {hasDiscount ? (
            <>
              <Style.StrikethroughPrice>{formatBrl(listUnit)}</Style.StrikethroughPrice>
              {formatBrl(payUnit)}
              <Style.DiscountBadge>-{discountPercent}%</Style.DiscountBadge>
            </>
          ) : (
            formatBrl(payUnit)
          )}
        </p>
        <p>
          <strong>Total: </strong>
          {hasDiscount ? (
            <>
              <Style.StrikethroughPrice>{formatBrl(listUnit * bottles)}</Style.StrikethroughPrice>
              {formatBrl(payUnit * bottles)}
            </>
          ) : (
            formatBrl(payUnit * bottles)
          )}
        </p>
      </Style.PriceContainer>
    </Style.RecipeProductContainer>
  )
}
